import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, AlertTriangle, DollarSign, TrendingUp, Clock,
  Copy, Check, RefreshCw, ExternalLink
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent } from "@/shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Separator } from "@/shared/components/ui/separator";
import { useInvoices, Invoice, InvoiceStatus } from "@/shared/hooks/useInvoices";
import { useMonthlyMerchantInvoices } from "@/shared/hooks/useMonthlyMerchantInvoices";
import { CustomerInput } from "@/shared/components/ui/customer-input";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { apiClient } from "@/shared/services/apiClient";
import { handleCheckout } from "@/shared/utils/checkoutHandler";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_FILTERS: (InvoiceStatus | 'ALL')[] = ['ALL', 'SENT', 'PAID', 'FAILED', 'REFUNDED', 'CANCELED'];

const US_STATES: { code: string; name: string; zip: string }[] = [
  { code: 'AL', name: 'Alabama', zip: '35004' },
  { code: 'AK', name: 'Alaska', zip: '99501' },
  { code: 'AZ', name: 'Arizona', zip: '85001' },
  { code: 'AR', name: 'Arkansas', zip: '72201' },
  { code: 'CA', name: 'California', zip: '90001' },
  { code: 'CO', name: 'Colorado', zip: '80201' },
  { code: 'CT', name: 'Connecticut', zip: '06101' },
  { code: 'DE', name: 'Delaware', zip: '19901' },
  { code: 'FL', name: 'Florida', zip: '32099' },
  { code: 'GA', name: 'Georgia', zip: '30301' },
  { code: 'HI', name: 'Hawaii', zip: '96801' },
  { code: 'ID', name: 'Idaho', zip: '83701' },
  { code: 'IL', name: 'Illinois', zip: '60601' },
  { code: 'IN', name: 'Indiana', zip: '46201' },
  { code: 'IA', name: 'Iowa', zip: '50301' },
  { code: 'KS', name: 'Kansas', zip: '66101' },
  { code: 'KY', name: 'Kentucky', zip: '40201' },
  { code: 'LA', name: 'Louisiana', zip: '70112' },
  { code: 'ME', name: 'Maine', zip: '04101' },
  { code: 'MD', name: 'Maryland', zip: '21201' },
  { code: 'MA', name: 'Massachusetts', zip: '02101' },
  { code: 'MI', name: 'Michigan', zip: '48201' },
  { code: 'MN', name: 'Minnesota', zip: '55101' },
  { code: 'MS', name: 'Mississippi', zip: '39201' },
  { code: 'MO', name: 'Missouri', zip: '63101' },
  { code: 'MT', name: 'Montana', zip: '59601' },
  { code: 'NE', name: 'Nebraska', zip: '68501' },
  { code: 'NV', name: 'Nevada', zip: '89101' },
  { code: 'NH', name: 'New Hampshire', zip: '03301' },
  { code: 'NJ', name: 'New Jersey', zip: '07001' },
  { code: 'NM', name: 'New Mexico', zip: '87501' },
  { code: 'NY', name: 'New York', zip: '10001' },
  { code: 'NC', name: 'North Carolina', zip: '27601' },
  { code: 'ND', name: 'North Dakota', zip: '58501' },
  { code: 'OH', name: 'Ohio', zip: '43201' },
  { code: 'OK', name: 'Oklahoma', zip: '73101' },
  { code: 'OR', name: 'Oregon', zip: '97201' },
  { code: 'PA', name: 'Pennsylvania', zip: '17101' },
  { code: 'RI', name: 'Rhode Island', zip: '02901' },
  { code: 'SC', name: 'South Carolina', zip: '29201' },
  { code: 'SD', name: 'South Dakota', zip: '57501' },
  { code: 'TN', name: 'Tennessee', zip: '37201' },
  { code: 'TX', name: 'Texas', zip: '75201' },
  { code: 'UT', name: 'Utah', zip: '84101' },
  { code: 'VT', name: 'Vermont', zip: '05401' },
  { code: 'VA', name: 'Virginia', zip: '23218' },
  { code: 'WA', name: 'Washington', zip: '98001' },
  { code: 'WV', name: 'West Virginia', zip: '25301' },
  { code: 'WI', name: 'Wisconsin', zip: '53701' },
  { code: 'WY', name: 'Wyoming', zip: '82001' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatMoney(amountCents: number) {
  return `$${(amountCents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function groupByMonth(invoices: Invoice[]) {
  const groups: { label: string; invoices: Invoice[] }[] = [];
  const seen = new Map<string, Invoice[]>();
  for (const inv of invoices) {
    const key = new Date(inv.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (!seen.has(key)) { seen.set(key, []); groups.push({ label: key, invoices: seen.get(key)! }); }
    seen.get(key)!.push(inv);
  }
  return groups;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status, refundedAmountCents, customerTotalCents }: {
  status: InvoiceStatus;
  refundedAmountCents?: number | null;
  customerTotalCents?: number | null;
}) {
  const map: Record<InvoiceStatus, { className: string; label: string }> = {
    DRAFT:      { className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Draft' },
    SENT:       { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300', label: 'Sent' },
    VIEWED:     { className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300', label: 'Viewed' },
    PROCESSING: { className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', label: 'Processing' },
    PAID:       { className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', label: status === 'PAID' && refundedAmountCents && refundedAmountCents > 0 && customerTotalCents && refundedAmountCents < customerTotalCents ? `Paid (${formatMoney(refundedAmountCents)} refunded)` : 'Paid' },
    FAILED:     { className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', label: 'Failed' },
    REFUNDED:   { className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300', label: 'Refunded' },
    CANCELED:   { className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Canceled' },
  };
  const v = map[status] ?? map.DRAFT;
  return <Badge className={`text-xs font-medium ${v.className}`}>{v.label}</Badge>;
}

// ─── Fee Tier label ───────────────────────────────────────────────────────────

const FEE_TIER_OPTIONS = [
  {
    value: 'split_fee',
    label: 'Option A — Customer Pays All',
    description: 'Customer pays invoice + $1.50 tech fee + 2.75% merchant fee. You receive base amount.',
  },
  {
    value: 'hybrid',
    label: 'Option B — You Pay Merchant Fee',
    description: 'Customer pays invoice + $1.50 tech fee. You absorb the 2.75% merchant fee (billed monthly).',
  },
  {
    value: 'absorbed',
    label: 'Option C — You Pay All',
    description: 'Customer pays base invoice amount only. You absorb both fees (billed monthly).',
  },
] as const;

type FeeTier = 'split_fee' | 'hybrid' | 'absorbed';

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    invoices,
    loading,
    refreshing,
    refresh,
    error: invoiceError,
    createInvoice,
    refundInvoice,
    voidInvoice,
    paidLast7DaysCents,
    sentLast7DaysCents,
    merchant,
  } = useInvoices();

  const { totalPendingCents, refresh: refreshMonthly } = useMonthlyMerchantInvoices();

  const { usage, loading: loadingSubscription } = useSubscription();

  // ── Fee config ──
  const [feeConfig, setFeeConfig] = useState({ technology_fee_cents: 150, merchant_fee_percent: 2.75 });
  useEffect(() => {
    apiClient.get<{ technology_fee_cents: number; merchant_fee_percent: number }>('/payments/fee-config')
      .then(data => { if (data) setFeeConfig(data); })
      .catch(() => {});
  }, []);

  // ── List state ──
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'ALL'>('ALL');

  // ── Selected invoice detail ──
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // ── Refund state ──
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [copiedPaymentLink, setCopiedPaymentLink] = useState(false);

  // ── Create invoice state ──
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerId: '',
    customerFirstName: '',
    customerLastName: '',
    amount: '',
    title: '',
    notes: '',
    feeTier: 'split_fee' as FeeTier,
    customerState: '',
  });

  // Payouts are ready if merchant exists and status is ACTIVE
  const payoutsNotSetup = !loading && (!merchant || merchant.status !== 'ACTIVE');
  const hasInvoiceSubscription = usage?.has_invoice_subscription || false;
  const invoiceSubscriptionStatus = usage?.invoice_subscription_status || 'none';
  const needsInvoiceSubscription = !hasInvoiceSubscription || invoiceSubscriptionStatus !== 'active';
  const needsSubscription = !loadingSubscription && usage && !usage.subscribed;
  const feesBlocked = totalPendingCents >= 5000;

  // ── Fee preview ──
  const amountCentsPreview = useMemo(() => {
    const v = parseFloat(formData.amount);
    return isNaN(v) || v <= 0 ? 0 : Math.round(v * 100);
  }, [formData.amount]);

  const feePreview = useMemo(() => {
    if (!amountCentsPreview) return null;
    const tech = feeConfig.technology_fee_cents;
    const merchantFee = Math.round(amountCentsPreview * feeConfig.merchant_fee_percent / 100);
    if (formData.feeTier === 'split_fee') {
      return { customerPays: amountCentsPreview + tech + merchantFee, youReceive: amountCentsPreview };
    } else if (formData.feeTier === 'hybrid') {
      return { customerPays: amountCentsPreview + tech, youReceive: amountCentsPreview - merchantFee };
    } else {
      return { customerPays: amountCentsPreview, youReceive: amountCentsPreview - tech - merchantFee };
    }
  }, [amountCentsPreview, formData.feeTier, feeConfig]);

  // ── Filtered/sorted invoices ──
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      if (statusFilter !== 'ALL' && inv.status !== statusFilter) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return inv.customer_name.toLowerCase().includes(q) ||
          inv.invoice_number.toLowerCase().includes(q) ||
          (inv.title ?? '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [invoices, statusFilter, searchTerm]);

  const invoiceGroups = useMemo(() => groupByMonth(filteredInvoices), [filteredInvoices]);

  // ── Handlers ──

  const handleRefresh = useCallback(async () => {
    await Promise.all([refresh(), refreshMonthly()]);
  }, [refresh, refreshMonthly]);

  const handleCustomerSelect = (customer: any) => {
    setFormData(prev => ({
      ...prev,
      customerName: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.name,
      customerId: customer.id,
      customerFirstName: customer.first_name || '',
      customerLastName: customer.last_name || '',
    }));
  };

  const handleCreateInvoice = async () => {
    if (!formData.customerName.trim()) {
      toast({ title: 'Customer is required', variant: 'destructive' }); return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Valid amount is required', variant: 'destructive' }); return;
    }
    if (!formData.title.trim()) {
      toast({ title: 'Title is required', variant: 'destructive' }); return;
    }
    if (formData.feeTier === 'split_fee' && !formData.customerState) {
      toast({ title: 'Customer state is required for Option A', variant: 'destructive' }); return;
    }

    const amountCents = Math.round(parseFloat(formData.amount) * 100);
    const stateEntry = US_STATES.find(s => s.code === formData.customerState);

    try {
      setCreatingInvoice(true);
      const result = await createInvoice({
        customer_id: formData.customerId || undefined,
        customer_name: formData.customerName,
        customer_first_name: formData.customerFirstName || undefined,
        customer_last_name: formData.customerLastName || undefined,
        amount_cents: amountCents,
        currency: 'USD',
        title: formData.title,
        notes: formData.notes || undefined,
        fee_tier: formData.feeTier,
        postal_code: stateEntry?.zip,
      } as any);

      if (result) {
        toast({ title: 'Invoice created successfully' });
        setShowCreateModal(false);
        setFormData({ customerName: '', customerId: '', customerFirstName: '', customerLastName: '', amount: '', title: '', notes: '', feeTier: 'split_fee', customerState: '' });
      } else {
        toast({ title: 'Failed to create invoice', variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: err.message || 'Failed to create invoice', variant: 'destructive' });
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleOpenInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setRefundAmount('');
    setRefundReason('');
    setSheetOpen(true);
  };

  const handleCopyPaymentLink = async () => {
    if (!selectedInvoice?.payment_url) return;
    await navigator.clipboard.writeText(selectedInvoice.payment_url);
    setCopiedPaymentLink(true);
    setTimeout(() => setCopiedPaymentLink(false), 2000);
  };

  const handleRefund = async () => {
    if (!selectedInvoice) return;
    const cents = Math.round(parseFloat(refundAmount) * 100);
    if (!cents || cents <= 0) {
      toast({ title: 'Enter a valid refund amount', variant: 'destructive' }); return;
    }
    const maxRefundable = (selectedInvoice.customer_total_cents || selectedInvoice.amount_cents) - (selectedInvoice.refunded_amount_cents || 0);
    if (cents > maxRefundable) {
      toast({ title: `Maximum refundable is ${formatMoney(maxRefundable)}`, variant: 'destructive' }); return;
    }
    try {
      setRefunding(true);
      const ok = await refundInvoice(selectedInvoice.id, cents, refundReason || undefined);
      if (ok) {
        toast({ title: 'Refund processed successfully' });
        setRefundAmount('');
        setRefundReason('');
        setSheetOpen(false);
      } else {
        toast({ title: 'Refund failed', variant: 'destructive' });
      }
    } finally {
      setRefunding(false);
    }
  };

  const handleVoid = async () => {
    if (!selectedInvoice) return;
    if (!window.confirm(`Void the payment for invoice #${selectedInvoice.invoice_number}? The customer will no longer be able to pay this invoice.`)) return;
    try {
      setVoiding(true);
      const ok = await voidInvoice(selectedInvoice.id);
      if (ok) {
        toast({ title: 'Payment voided successfully' });
        setSheetOpen(false);
      } else {
        toast({ title: 'Failed to void payment', description: invoiceError || undefined, variant: 'destructive' });
      }
    } finally {
      setVoiding(false);
    }
  };

  const handleSetupPayouts = async () => {
    try {
      setOnboardingLoading(true);
      const data = await apiClient.post<{ merchant: any; onboarding_url?: string; message: string; already_active?: boolean }>('/payments/setup-forward-merchant', {});
      if (data?.already_active || data?.merchant?.status === 'ACTIVE') {
        toast({ title: 'Merchant Already Active', description: 'Your merchant account is active.' });
        await refresh();
        return;
      }
      if (!data?.onboarding_url) throw new Error('Failed to get onboarding URL');
      const w = window.open(data.onboarding_url, '_blank');
      if (!w) { toast({ title: 'Popup Blocked', variant: 'destructive' }); return; }
      toast({ title: 'Onboarding Started', description: "Complete it in the new tab. Use 'Refresh' when done." });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handlePurchaseInvoiceSubscription = async () => {
    try {
      setPurchaseLoading(true);
      const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
      const data = await apiClient.post<{ url: string }>('/payments/create-invoice-subscription-checkout', {
        successUrl: `${baseUrl}/invoice-subscription-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${baseUrl}/invoice-subscription-error?message=Payment was canceled`,
      });
      if (!data?.url) throw new Error('Failed to create checkout');
      handleCheckout({
        checkoutUrl: data.url,
        onSuccess: async () => { setPurchaseLoading(false); toast({ title: 'Payment Successful!' }); await refresh(); },
        onError: (msg) => { toast({ title: 'Payment Failed', description: msg, variant: 'destructive' }); setPurchaseLoading(false); },
        onClose: () => { setPurchaseLoading(false); refresh(); },
      });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setPurchaseLoading(false);
    }
  };

  // ── $50 fee gate — full-screen blocker ────────────────────────────────────

  if (feesBlocked) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center">
        <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-5">
          <AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-400" />
        </div>
        <div className="space-y-2 max-w-md">
          <h2 className="text-xl font-bold text-foreground">Invoice Access Paused</h2>
          <p className="text-muted-foreground text-sm">
            You have <span className="font-semibold text-red-600">{formatMoney(totalPendingCents)}</span> in outstanding
            processing fees. Please clear your balance to continue creating and managing invoices.
          </p>
        </div>
        <Button onClick={() => navigate('/invoices/monthly-fees')} className="bg-red-600 hover:bg-red-700 text-white">
          Pay Fees Now
        </Button>
      </div>
    );
  }

  // ── Setup gates ───────────────────────────────────────────────────────────

  const canCreate = !needsSubscription && !needsInvoiceSubscription && !payoutsNotSetup && !feesBlocked;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage and track your customer invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)} disabled={!canCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Setup banners */}
      {needsSubscription && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 p-4 flex items-center justify-between">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">A Wlkthru Pro subscription is required to use invoices.</p>
          <Button size="sm" variant="outline" onClick={() => navigate('/profile?tab=billing')}>Subscribe</Button>
        </div>
      )}
      {!needsSubscription && needsInvoiceSubscription && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 p-4 flex items-center justify-between">
          <p className="text-sm text-blue-800 dark:text-blue-300">Add the Invoice add-on to start sending payment requests.</p>
          <Button size="sm" onClick={handlePurchaseInvoiceSubscription} disabled={purchaseLoading}>
            {purchaseLoading ? 'Loading...' : 'Add Invoice Plan'}
          </Button>
        </div>
      )}
      {!needsSubscription && !needsInvoiceSubscription && payoutsNotSetup && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-900/20 p-4 flex items-center justify-between">
          <p className="text-sm text-orange-800 dark:text-orange-300">Complete merchant onboarding to accept payments.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleRefresh}>Refresh Status</Button>
            <Button size="sm" onClick={handleSetupPayouts} disabled={onboardingLoading}>
              {onboardingLoading ? 'Loading...' : 'Set Up Payouts'}
            </Button>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Clock className="h-3.5 w-3.5" /> Sent (7d)
            </div>
            <p className="text-xl font-bold">{formatMoney(sentLast7DaysCents)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <TrendingUp className="h-3.5 w-3.5" /> Paid (7d)
            </div>
            <p className="text-xl font-bold text-green-600">{formatMoney(paidLast7DaysCents)}</p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:border-red-300 transition-colors"
          onClick={() => navigate('/invoices/monthly-fees')}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <DollarSign className="h-3.5 w-3.5" /> Fees Due
            </div>
            <p className={`text-xl font-bold ${totalPendingCents > 0 ? 'text-red-600' : 'text-foreground'}`}>
              {formatMoney(totalPendingCents)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by customer, invoice #, or title..."
            className="pl-9"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-1">
          {STATUS_FILTERS.map(s => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
              className="text-xs h-8 px-3"
            >
              {s === 'ALL' ? 'All' : s === 'CANCELED' ? 'Voided' : s.charAt(0) + s.slice(1).toLowerCase()}
            </Button>
          ))}
        </div>
      </div>

      {/* Invoice List */}
      {loading ? (
        <div className="text-center py-16 text-muted-foreground text-sm">Loading invoices...</div>
      ) : filteredInvoices.length === 0 ? (
        <div className="text-center py-16 space-y-2">
          <p className="text-muted-foreground font-medium">No invoices found</p>
          <p className="text-sm text-muted-foreground">
            {canCreate ? 'Create your first invoice using the button above.' : 'Complete setup above to start creating invoices.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {invoiceGroups.map(group => (
            <div key={group.label}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group.label}</p>
              <div className="rounded-lg border overflow-hidden divide-y">
                {group.invoices.map(invoice => (
                  <button
                    key={invoice.id}
                    className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => handleOpenInvoice(invoice)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{invoice.customer_name}</span>
                        {invoice.title && (
                          <span className="text-xs text-muted-foreground truncate hidden sm:block">— {invoice.title}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">{invoice.invoice_number}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">{formatDate(invoice.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <StatusBadge
                        status={invoice.status}
                        refundedAmountCents={invoice.refunded_amount_cents}
                        customerTotalCents={invoice.customer_total_cents}
                      />
                      <span className="text-sm font-semibold tabular-nums">
                        {formatMoney(invoice.customer_total_cents ?? invoice.amount_cents)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Invoice Detail Sheet ─────────────────────────────────────────────── */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {selectedInvoice && (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle className="flex items-center gap-2">
                  Invoice {selectedInvoice.invoice_number}
                  <StatusBadge
                    status={selectedInvoice.status}
                    refundedAmountCents={selectedInvoice.refunded_amount_cents}
                    customerTotalCents={selectedInvoice.customer_total_cents}
                  />
                </SheetTitle>
              </SheetHeader>

              {/* Customer & dates */}
              <div className="space-y-1 mb-4">
                <p className="text-sm font-semibold">{selectedInvoice.customer_name}</p>
                {selectedInvoice.title && <p className="text-sm text-muted-foreground">{selectedInvoice.title}</p>}
                {selectedInvoice.notes && <p className="text-xs text-muted-foreground">{selectedInvoice.notes}</p>}
                <p className="text-xs text-muted-foreground">Created: {formatDate(selectedInvoice.created_at)}</p>
                {selectedInvoice.paid_at && <p className="text-xs text-muted-foreground">Paid: {formatDate(selectedInvoice.paid_at)}</p>}
                {selectedInvoice.canceled_at && <p className="text-xs text-muted-foreground">Voided: {formatDate(selectedInvoice.canceled_at)}</p>}
              </div>

              <Separator className="my-4" />

              {/* Amount breakdown */}
              <div className="space-y-2 mb-4 bg-muted/30 rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoice Amount</span>
                  <span>{formatMoney(selectedInvoice.amount_cents)}</span>
                </div>
                {(selectedInvoice.technology_fee_cents ?? 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Technology Fee</span>
                    <span>{formatMoney(selectedInvoice.technology_fee_cents!)}</span>
                  </div>
                )}
                {(selectedInvoice.merchant_fee_cents ?? 0) > 0 &&
                  selectedInvoice.fee_tier === 'split_fee' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Merchant Fee</span>
                    <span>{formatMoney(selectedInvoice.merchant_fee_cents!)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-sm font-semibold">
                  <span>Customer Total</span>
                  <span>{formatMoney(selectedInvoice.customer_total_cents ?? selectedInvoice.amount_cents)}</span>
                </div>
                {selectedInvoice.fee_tier && (
                  <p className="text-xs text-muted-foreground">
                    {selectedInvoice.fee_tier === 'split_fee' ? 'Option A — Customer pays all fees'
                      : selectedInvoice.fee_tier === 'hybrid' ? 'Option B — You absorb merchant fee'
                      : 'Option C — You absorb all fees'}
                  </p>
                )}
              </div>

              {/* Void info if canceled */}
              {selectedInvoice.status === 'CANCELED' && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 p-3 mb-4 text-sm">
                  <p className="font-medium text-gray-800 dark:text-gray-300">
                    Payment voided — {(selectedInvoice.voided_amount_cents ?? 0) > 0
                      ? `${formatMoney(selectedInvoice.voided_amount_cents!)} voided`
                      : `${formatMoney(selectedInvoice.amount_cents)} voided`}
                  </p>
                  {selectedInvoice.canceled_at && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Voided on {formatDate(selectedInvoice.canceled_at)}
                    </p>
                  )}
                </div>
              )}

              {/* Refund info if already refunded */}
              {(selectedInvoice.refunded_amount_cents ?? 0) > 0 && (
                <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 p-3 mb-4 text-sm">
                  <p className="font-medium text-orange-800 dark:text-orange-300">
                    {formatMoney(selectedInvoice.refunded_amount_cents!)} refunded
                  </p>
                  {selectedInvoice.refund_reason && (
                    <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">Reason: {selectedInvoice.refund_reason}</p>
                  )}
                </div>
              )}

              {/* Payment link */}
              {selectedInvoice.payment_url && selectedInvoice.status !== 'PAID' && selectedInvoice.status !== 'REFUNDED' && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Payment Link</p>
                  <div className="flex gap-2">
                    <Input value={selectedInvoice.payment_url} readOnly className="text-xs h-8" />
                    <Button size="sm" variant="outline" className="h-8 px-2" onClick={handleCopyPaymentLink}>
                      {copiedPaymentLink ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => window.open(selectedInvoice.payment_url!, '_blank')}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Void section — only for SENT/PROCESSING invoices with a payment intent */}
              {['SENT', 'PROCESSING'].includes(selectedInvoice.status) && selectedInvoice.fwd_payment_intent_id && (
                <div className="rounded-lg border border-red-200 dark:border-red-800 p-4 mb-4">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Void Payment</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Cancels the payment link so the customer can no longer pay this invoice.
                  </p>
                  <button
                    onClick={handleVoid}
                    disabled={voiding}
                    className="w-full rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {voiding ? 'Voiding…' : 'Void Payment'}
                  </button>
                </div>
              )}

              {/* Refund section — only for PAID with remaining refundable amount */}
              {selectedInvoice.status === 'PAID' && (() => {
                const baseCents = selectedInvoice.amount_cents;
                const total = selectedInvoice.customer_total_cents ?? baseCents;
                const techFee = selectedInvoice.technology_fee_cents ?? 0;
                const merchantFee = selectedInvoice.merchant_fee_cents ?? 0;
                const fees = total - baseCents;
                const alreadyRefunded = selectedInvoice.refunded_amount_cents ?? 0;
                const maxRefundable = total - alreadyRefunded;
                if (maxRefundable <= 0) return null;
                const fullRefundValue = (maxRefundable / 100).toFixed(2);
                const isFullRefund = refundAmount === fullRefundValue;
                const isPartialRefund = refundAmount !== '' && refundAmount !== fullRefundValue;
                return (
                  <div className="space-y-3 pt-2 border-t">
                    <p className="text-sm font-semibold">Issue Refund</p>

                    {/* Payment breakdown */}
                    <div className="rounded-lg bg-muted/40 p-3 space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Invoice amount</span>
                        <span>{formatMoney(baseCents)}</span>
                      </div>
                      {fees > 0 && techFee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Technology fee</span>
                          <span>+{formatMoney(techFee)}</span>
                        </div>
                      )}
                      {fees > 0 && merchantFee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Merchant fee</span>
                          <span>+{formatMoney(merchantFee)}</span>
                        </div>
                      )}
                      {fees > 0 && techFee === 0 && merchantFee === 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fees (surcharge/tech)</span>
                          <span>+{formatMoney(fees)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold border-t pt-1.5 mt-1">
                        <span>Customer paid</span>
                        <span>{formatMoney(total)}</span>
                      </div>
                      {alreadyRefunded > 0 && (
                        <div className="flex justify-between text-amber-600 dark:text-amber-400">
                          <span>Already refunded</span>
                          <span>-{formatMoney(alreadyRefunded)}</span>
                        </div>
                      )}
                    </div>

                    {/* Full / Partial toggle */}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setRefundAmount(fullRefundValue)}
                        className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                          isFullRefund
                            ? 'border-destructive bg-destructive/8 text-destructive'
                            : 'border-border bg-background text-muted-foreground hover:border-destructive/50'
                        }`}
                      >
                        <p className="text-xs font-semibold">Full Refund</p>
                        <p className="text-xs">{formatMoney(maxRefundable)}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setRefundAmount('')}
                        className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                          isPartialRefund
                            ? 'border-destructive bg-destructive/8 text-destructive'
                            : 'border-border bg-background text-muted-foreground hover:border-destructive/50'
                        }`}
                      >
                        <p className="text-xs font-semibold">Partial Refund</p>
                        <p className="text-xs">Custom amount</p>
                      </button>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Refund Amount (USD)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={fullRefundValue}
                        placeholder="0.00"
                        value={refundAmount}
                        onChange={e => setRefundAmount(e.target.value)}
                        className="h-8 text-sm"
                      />
                      <p className="text-xs text-muted-foreground">Max refundable: {formatMoney(maxRefundable)} (amount customer paid)</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Reason (optional)</Label>
                      <Textarea
                        placeholder="Customer request, incorrect amount, etc."
                        value={refundReason}
                        onChange={e => setRefundReason(e.target.value)}
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    <Button
                      className="w-full"
                      variant="destructive"
                      onClick={handleRefund}
                      disabled={refunding || !refundAmount}
                    >
                      {refunding ? 'Processing...' : `Refund ${refundAmount ? formatMoney(Math.round(parseFloat(refundAmount) * 100)) : ''}`}
                    </Button>
                  </div>
                );
              })()}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Create Invoice Modal ──────────────────────────────────────────────── */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Send a payment request to your customer</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Customer */}
            <div className="space-y-1">
              <Label>Customer *</Label>
              <CustomerInput
                placeholder="Search customer by name, phone, or email"
                value={formData.customerName}
                onChange={v => setFormData(prev => ({ ...prev, customerName: v }))}
                onCustomerSelect={handleCustomerSelect}
                field="name"
              />
            </div>

            {/* Amount */}
            <div className="space-y-1">
              <Label htmlFor="amount">Invoice Amount (USD) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={formData.amount}
                onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>

            {/* Title */}
            <div className="space-y-1">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Kitchen sink repair"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any details for the customer..."
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Fee Tier */}
            <div className="space-y-2">
              <Label>Who Pays Processing Fees *</Label>
              <div className="space-y-2">
                {FEE_TIER_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.feeTier === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/30'
                    }`}
                  >
                    <input
                      type="radio"
                      name="feeTier"
                      value={opt.value}
                      checked={formData.feeTier === opt.value}
                      onChange={() => setFormData(prev => ({ ...prev, feeTier: opt.value, customerState: '' }))}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Customer State (only for Option A) */}
            {formData.feeTier === 'split_fee' && (
              <div className="space-y-1">
                <Label>Customer State *</Label>
                <Select
                  value={formData.customerState}
                  onValueChange={v => setFormData(prev => ({ ...prev, customerState: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state..." />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Used to determine restricted state / dual pricing rules.</p>
              </div>
            )}

            {/* Fee Preview */}
            {feePreview && (
              <div className="rounded-lg bg-muted/40 p-3 space-y-1.5 text-sm border">
                <p className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Fee Preview</p>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice Amount</span>
                  <span>{formatMoney(amountCentsPreview)}</span>
                </div>
                {/* Option A: customer pays both fees */}
                {formData.feeTier === 'split_fee' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tech Fee (customer pays)</span>
                      <span>+{formatMoney(feeConfig.technology_fee_cents)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Merchant Fee {feeConfig.merchant_fee_percent}% (customer pays)</span>
                      <span>+{formatMoney(feePreview.customerPays - amountCentsPreview - feeConfig.technology_fee_cents)}</span>
                    </div>
                  </>
                )}
                {/* Option B: customer pays tech fee only */}
                {formData.feeTier === 'hybrid' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tech Fee (customer pays)</span>
                      <span>+{formatMoney(feeConfig.technology_fee_cents)}</span>
                    </div>
                    <div className="flex justify-between text-amber-600 dark:text-amber-400">
                      <span>Merchant Fee {feeConfig.merchant_fee_percent}% (you absorb)</span>
                      <span>-{formatMoney(amountCentsPreview - feePreview.youReceive)}</span>
                    </div>
                  </>
                )}
                {/* Option C: customer pays base only */}
                {formData.feeTier === 'absorbed' && (
                  <div className="flex justify-between text-amber-600 dark:text-amber-400">
                    <span>All fees (you absorb)</span>
                    <span>-{formatMoney(amountCentsPreview - feePreview.youReceive)}</span>
                  </div>
                )}
                <div className="border-t pt-1.5 mt-1 space-y-1">
                  <div className="flex justify-between font-semibold">
                    <span>Customer Pays</span>
                    <span>{formatMoney(feePreview.customerPays)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>You Receive</span>
                    <span className="text-green-700 dark:text-green-400">{formatMoney(feePreview.youReceive)}</span>
                  </div>
                </div>
                {(formData.feeTier === 'hybrid' || formData.feeTier === 'absorbed') && (
                  <p className="text-xs text-muted-foreground">Processing fees billed monthly to you.</p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowCreateModal(false)} disabled={creatingInvoice}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleCreateInvoice} disabled={creatingInvoice}>
                {creatingInvoice ? 'Creating...' : 'Create Invoice'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
